import type { AhastraAction, ISODateString, ActionSizePreference } from "../../domain/src";

export type ActionTimeChoice = "now" | "today" | "tonight" | "tomorrow" | "this_week" | "next_week" | "manual" | "later";
export type ActionPriorityChoice = "low" | "medium" | "high";
export type RecurrenceChoice = "none" | "daily" | "weekly" | "monthly";

export interface BreakIntoStepsResult {
  readonly originalTitle: string;
  readonly steps: readonly string[];
}

export interface InterpretNewActionInput {
  readonly rawInput: string;
  readonly userTime: ActionTimeChoice;
  readonly userPriority: ActionPriorityChoice;
  readonly recurrence?: RecurrenceChoice;
  readonly manualDate?: ISODateString;
  readonly manualTime?: string;
  readonly now: ISODateString;
}

export interface InterpretedAction {
  readonly title: string;
  readonly timingLabel: string;
  readonly priorityLabel: "Laag" | "Medium" | "Hoog";
  readonly recurrenceLabel?: string;
  readonly dueDate?: ISODateString;
  readonly lockedDay?: ISODateString;
  readonly lockedAt?: ISODateString;
  readonly nonNegotiable: boolean;
  readonly size: ActionSizePreference;
}

/**
 * Rule-based MVP1 micro-step boundary.
 * Replaceable later by a local LLM without changing UI, store, or Priority Grid callers.
 */
export function breakIntoSteps(action: string): BreakIntoStepsResult {
  const originalTitle = normalizeTitle(action);
  const lower = originalTitle.toLowerCase();
  if (/\b(huur|rent|rekening|factuur|invoice|betaling|bank|belasting|tax)\b/.test(lower)) {
    return result(originalTitle, ["Open je betaalapp", "Controleer bedrag en ontvanger", "Geef zelf het laatste akkoord"]);
  }
  if (/\b(tandarts|dokter|huisarts|afspraak|bellen|bel)\b/.test(lower)) {
    return result(originalTitle, ["Zoek het telefoonnummer", "Leg één zin klaar", "Bel of plan de afspraak"]);
  }
  if (/\b(mail|email|bericht|app|review|klant|thomas)\b/.test(lower)) {
    return result(originalTitle, ["Open het juiste bericht", "Schrijf alleen de eerste zin", "Verstuur pas als jij akkoord bent"]);
  }
  if (/\b(aanrecht|was|opruim|opruimen|schoon|keuken|boodschap|boodschappen|vuilnis)\b/.test(lower)) {
    return result(originalTitle, ["Kies één plek", "Doe alleen de eerste kleine handeling", "Stop zodra deze stap klaar is"]);
  }
  if (/\b(loop|wandelen|buiten|water|adem|rust|slaap|gezondheid)\b/.test(lower)) {
    return result(originalTitle, ["Sta op", "Maak de drempel kleiner", "Doe alleen de eerste minuut"]);
  }
  if (/\b(uitzoeken|regelen|voorbereiden|organiseren|plan|project|uitwerken)\b/.test(lower)) {
    return result(originalTitle, ["Schrijf de eerste losse stap op", "Kies wat vandaag genoeg is", "Doe alleen die stap"]);
  }
  return result(originalTitle, [`Open: ${originalTitle}`, "Doe alleen de eerste kleine stap", "Beslis daarna of dit genoeg is"]);
}

/**
 * Rule-based MVP1 add-action interpretation boundary.
 * MVP1 deliberately keeps deterministic explicit choices as the fallback boundary.
 */
export function interpretNewAction(input: InterpretNewActionInput): InterpretedAction {
  const title = normalizeTitle(input.rawInput);
  const today = input.now.slice(0, 10);
  const tomorrow = addDays(today, 1);
  const manualDay = normalizeDay(input.manualDate);
  const manualTime = normalizeTime(input.manualTime);
  const dueDate = dueDateFor(input.userTime, today, tomorrow, manualDay);
  const lockedDay = lockedDayFor(input.userTime, today, tomorrow, dueDate);
  const lockedAt = lockedAtFor(input.userTime, today, dueDate, manualTime);
  const high = input.userPriority === "high";
  return {
    title,
    timingLabel: timingLabel(input.userTime),
    priorityLabel: priorityLabel(input.userPriority),
    recurrenceLabel: recurrenceLabel(input.recurrence ?? "none"),
    dueDate,
    lockedDay,
    lockedAt,
    nonNegotiable: high && input.userTime !== "later",
    size: sizeForPriority(input.userPriority),
  };
}

/**
 * Rule-based MVP1 explanation boundary.
 * Keep this out of the UI as a prominent line; callers may expose it only behind a small info affordance.
 */
export function whyNow(action: Pick<AhastraAction, "title" | "dueDate" | "lockedAt" | "lockedDay" | "effectiveStartTime" | "nonNegotiable" | "skipCount" | "astraId" | "source">): string {
  if (action.effectiveStartTime || action.lockedAt || action.lockedDay) return "Heeft een vaste plek in vandaag.";
  if (action.nonNegotiable || action.dueDate) return "Draagt vandaag direct mee.";
  if (action.astraId && (action.skipCount ?? 0) > 0) return "Blijft belangrijk, ook zonder haast.";
  if (action.astraId) return "Helpt richting bewaren.";
  if (action.source === "calendar") return "Staat in je agenda.";
  return "Past bij wat vandaag vraagt.";
}

function result(originalTitle: string, steps: readonly string[]): BreakIntoStepsResult {
  return { originalTitle, steps };
}

function normalizeTitle(input: string): string {
  return String(input || "").trim().replace(/\s+/g, " ").replace(/[.!?]+$/, "") || "Nieuwe actie";
}

function timingLabel(choice: ActionTimeChoice): string {
  if (choice === "now") return "Nu";
  if (choice === "today") return "Vandaag";
  if (choice === "tonight") return "Vanavond";
  if (choice === "tomorrow") return "Morgen";
  if (choice === "this_week") return "Deze week";
  if (choice === "next_week") return "Volgende week";
  if (choice === "manual") return "Zelf gekozen";
  return "Later";
}

function priorityLabel(choice: ActionPriorityChoice): "Laag" | "Medium" | "Hoog" {
  if (choice === "high") return "Hoog";
  if (choice === "low") return "Laag";
  return "Medium";
}

function recurrenceLabel(choice: RecurrenceChoice): string | undefined {
  if (choice === "daily") return "Elke dag";
  if (choice === "weekly") return "Elke week";
  if (choice === "monthly") return "Elke maand";
  return undefined;
}

function sizeForPriority(choice: ActionPriorityChoice): ActionSizePreference {
  if (choice === "low") return "tiny";
  if (choice === "high") return "small";
  return "small";
}

function dueDateFor(choice: ActionTimeChoice, today: string, tomorrow: string, manualDay: string | undefined): ISODateString | undefined {
  if (choice === "now" || choice === "today" || choice === "tonight") return today;
  if (choice === "tomorrow") return tomorrow;
  if (choice === "this_week") return addDays(today, 6);
  if (choice === "next_week") return addDays(today, 7);
  if (choice === "manual") return manualDay;
  return undefined;
}

function lockedDayFor(choice: ActionTimeChoice, today: string, tomorrow: string, dueDate: string | undefined): ISODateString | undefined {
  if (choice === "now") return today;
  if (choice === "tomorrow") return tomorrow;
  if (choice === "tonight" || choice === "manual") return dueDate;
  return undefined;
}

function lockedAtFor(choice: ActionTimeChoice, today: string, dueDate: string | undefined, manualTime: string | undefined): ISODateString | undefined {
  if (choice === "tonight") return `${today}T20:00:00.000`;
  if (choice === "manual" && dueDate && manualTime) return `${dueDate}T${manualTime}:00.000`;
  return undefined;
}

function normalizeDay(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match?.[0];
}

function normalizeTime(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const match = value.match(/^([01]\d|2[0-3]):[0-5]\d$/);
  return match?.[0];
}

function addDays(day: string, amount: number): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}
